/**
 * `/studio` — `mockups/studio/analytics.html`, the studio's front page.
 *
 * **A page composes primitives, states facts it was given, and computes nothing.**
 * Every figure here comes off one of the seven `analytics_*` reads in `data.ts`;
 * nothing on this page divides, adds or compares two amounts except the one
 * six-month subtotal DESIGN.md §Non-negotiable licenses, which is added with
 * `packages/shared`'s `sum` and reconciles to the rows above it.
 *
 * The layout is the mockup's: a hero region, then three sections — recurring revenue,
 * revenue by billing model, the under-scoped guardrail — and a mix-and-pipeline
 * region. `HeroRegion` carries the page's one `.hero-figure` (P1, and
 * `expectOneHero`), and §3 carries its one `HowThisWorks` (P7).
 *
 * **Every read is `inline`.** Eleven panels over seven reads is seven ways for this
 * page to be half-there, and `Panel` renders each failure where it happened. The
 * whole page waits only for the *first* answer, and until then it is one muted line —
 * CONTRACT.md §2.14: "ONE muted line. No icon, no panel, no paragraph."
 *
 * **The period control moves the guardrail and nothing else, and the page says so.**
 * Only `analytics_client_health` and `analytics_effective_rate` take a period; MRR,
 * revenue by model, tier mix, package mix and pipeline each answer as of now. So the
 * seg is labelled for what it controls, §3's head names the month it was answered
 * for, §4's head says it is today's picture, and the topbar eyebrow does **not**
 * carry a month — a page titled "Studio · June 2026" above August's MRR hero is a
 * lie told by juxtaposition. The report asks for a period on the other five reads.
 */

import { PeriodSchema, parsePeriod, periodOf } from '@fareast/shared'
import { z } from 'zod'

import { PeriodSeg } from '../../../components/client/index.js'
import { Button } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { useFormat } from '../../../format/useFormat.js'
import { now } from '../../../integrations/clock.js'
import { useSearchState } from '../../../search/useSearchState.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { analyticsSections, csvFilename, downloadCsv, toCsv } from './csv.js'
import { hasAnyAnswer, hasAnyData, useAnalytics } from './data.js'
import { HeroRegion } from './HeroRegion.js'
import { MixSection } from './MixSection.js'
import { MrrSection } from './MrrSection.js'
import { RevenueSection } from './RevenueSection.js'
import { UnderScopedSection } from './UnderScopedSection.js'

export const AnalyticsSearchSchema = z.object({
  period: PeriodSchema.optional(),
})

/** What the seg actually moves. A control with no name is a puzzle (`Seg`'s own rule). */
const SEG_LABEL = 'Guardrail month'

export function AnalyticsPage() {
  const [search, setSearch] = useSearchState(AnalyticsSearchSchema)
  const format = useFormat()
  const latest = periodOf(now())
  const period = search.period ?? latest
  const reads = useAnalytics(period)

  return (
    <>
      <PageTitle
        eyebrow="Studio"
        title="Analytics"
        actions={
          <>
            <PeriodSeg
              label={SEG_LABEL}
              value={period}
              latest={latest}
              onChange={(next) => {
                setSearch({ period: parsePeriod(next) })
              }}
            />
            <Button
              disabled={!hasAnyData(reads)}
              onClick={() => {
                downloadCsv(csvFilename(period), toCsv(analyticsSections(reads, format)))
              }}
            >
              Export
            </Button>
          </>
        }
      />

      {hasAnyAnswer(reads) ? (
        <>
          <HeroRegion reads={reads} period={period} />
          <MrrSection query={reads.mrr} />
          <RevenueSection query={reads.revenue} />
          <UnderScopedSection health={reads.health} rate={reads.rate} />
          <MixSection
            tierMix={reads.tierMix}
            packageMix={reads.packageMix}
            pipeline={reads.pipeline}
          />
        </>
      ) : (
        <Empty>Loading the studio's numbers.</Empty>
      )}
    </>
  )
}
